import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Context } from 'scala'
import { FeaturesPlayer } from 'player'
import { Task } from '../../../../types'
import { DropdownChangeOperations as DropdownChangeOperationsV2 } from '../../dropdown-change-operations/2'
import { UseModalPaywall } from '../../../../hooks/event/use-modal-paywall'
import {
  useUserVariantUpload,
  Variants
} from '../../../../hooks/user/use-user-variant-upload'
import { DropdownChangeOperations as DropdownChangeOperationsV1 } from '../../dropdown-change-operations/1'
import { UseChangeOperations } from '../../../../hooks/tasks/use-upgradability-new/use-change-operations'

type UseFeaturesChangeOperationsProps = {
  task: Task
  modalPaywall: UseModalPaywall
  changeOperations: UseChangeOperations
}

type UseFeatures = FeaturesPlayer['ChangeOperations']

export const useFeaturesChangeOperations = ({
  task,
  modalPaywall,
  changeOperations
}: UseFeaturesChangeOperationsProps): UseFeatures => {
  const [variant, setVariant] = useState<Variants | null>(null)
  const [loaded, setLoaded] = useState(false)
  const { user } = useContext(Context)

  const { onGetVariants } = useUserVariantUpload()

  const run = useCallback(async () => {
    const type = await onGetVariants()

    setVariant(type)
  }, [onGetVariants])

  useEffect(() => {
    if (!user || loaded) return

    setLoaded(true)
    run()
  }, [user, run, setLoaded, loaded])

  const isDemo = useMemo(() => task?.isDemo, [task?.isDemo])

  const uploadsDisableOnWeb = useMemo(
    () => user?.featureFlags.uploadsTemporarilyUnavailableOnWeb,
    [user?.featureFlags]
  )

  const ChangeOperations = useMemo(() => {
    if (uploadsDisableOnWeb || isDemo) {
      return null
    }

    if (variant?.CustomSeparate || variant?.CustomSeparateLimited) {
      return (
        <DropdownChangeOperationsV2
          summary={task?.summary?.v1}
          taskId={task?.id}
          modalPaywall={modalPaywall}
          isLimited={variant?.CustomSeparateLimited}
        />
      )
    }
    if (variant?.CustomSeparateFreeUser) {
      return (
        <DropdownChangeOperationsV1
          summary={task?.summary?.v1}
          modalPaywall={modalPaywall}
          changeOperations={changeOperations}
          taskId={task?.id}
        />
      )
    }

    return null
  }, [
    task,
    isDemo,
    modalPaywall,
    variant,
    changeOperations,
    uploadsDisableOnWeb
  ])

  return ChangeOperations
}
